import { Box } from '@chakra-ui/react';
import { Badge, BadgeProps, Popover, Tooltip } from '@melio/penny';
import { useAnalytics } from '@melio/platform-analytics';
import { Vendor, VendorAutoPayStatusEnum, VendorEBillStatusEnum } from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMelioIntl } from '@melio/platform-i18n';
import { MouseEventHandler } from 'react';

export type EbillCellBadgeProps = {
  vendor: Vendor;
  onEBillSubscribeClick?: (vendorId: string) => void;
  onAutoPayActivationClick?: (vendorId: string) => void;
  isReadOnly?: boolean;
};

export const EbillCellBadge = ({
  vendor,
  onEBillSubscribeClick,
  onAutoPayActivationClick,
  isReadOnly,
}: EbillCellBadgeProps) => {
  const { formatMessage } = useMelioIntl();

  const { title, actionProps, description } =
    usePopoverProps(vendor, onEBillSubscribeClick, onAutoPayActivationClick) ?? {};

  const renderBadge = (ariaLabel?: string) => (
    <Box
      as={Badge}
      type="status"
      status={getBadgeStatus(vendor)}
      label={formatMessage('activities.payDashboard.eBillCellBadge.eBill')}
      data-testid={`ebill-cell-badge-${vendor.id}`}
      aria-label={ariaLabel}
      tabIndex={0}
    />
  );

  if (actionProps && !isReadOnly) {
    return (
      <Popover
        title={title ?? ''}
        description={description ?? ''}
        actionProps={actionProps}
        data-testid="ebill-badge-popover"
      >
        {renderBadge()}
      </Popover>
    );
  } else if (description) {
    return (
      <Tooltip label={description} placement="top">
        {renderBadge(description)}
      </Tooltip>
    );
  }

  return renderBadge();
};

export const usePopoverProps = (
  vendor: Vendor,
  onEBillSubscribeClick?: (vendorId: string) => void,
  onAutoPayActivationClick?: (vendorId: string) => void
): null | {
  title?: string;
  description: string;
  actionProps?: {
    label: string;
    onClick: MouseEventHandler<Element>;
  };
} => {
  const { formatMessage } = useMelioIntl();
  const [isAutoPayEnabled] = useFeature(FeatureFlags.AutoPay, false);
  const { track } = useAnalytics();

  if (!onEBillSubscribeClick || !onAutoPayActivationClick) {
    return null;
  }

  const handleEBillSubscribeClick = () => {
    track('Dashboard', 'Click', {
      Cta: 'subscribe',
      VendorId: vendor.id,
    });
    onEBillSubscribeClick(vendor.id);
  };

  const handleAutoPayActivationClick = () => {
    track('Dashboard', 'Click', {
      Cta: 'autopay-set-up',
      VendorId: vendor.id,
    });
    onAutoPayActivationClick(vendor.id);
  };

  switch (vendor.eBillStatus) {
    case VendorEBillStatusEnum.Capable:
      return {
        title: formatMessage('activities.payDashboard.eBillCellBadge.popover.capable.title'),
        description: formatMessage('activities.payDashboard.eBillCellBadge.popover.capable.description'),
        actionProps: {
          label: formatMessage('activities.payDashboard.eBillCellBadge.popover.capable.subscribe'),
          onClick: handleEBillSubscribeClick,
        },
      };
    case VendorEBillStatusEnum.Pending:
      return { description: formatMessage('activities.payDashboard.eBillCellBadge.popover.pending.description') };
    case VendorEBillStatusEnum.Rejected:
      return { description: formatMessage('activities.payDashboard.eBillCellBadge.popover.failed.description') };
    case VendorEBillStatusEnum.Active: {
      if (isAutoPayEnabled && vendor.autoPayStatus === VendorAutoPayStatusEnum.Capable) {
        return {
          title: formatMessage('activities.payDashboard.autoPayCellBadge.popover.capable.title'),
          description: formatMessage('activities.payDashboard.autoPayCellBadge.popover.capable.description'),
          actionProps: {
            label: formatMessage('activities.payDashboard.autoPayCellBadge.popover.capable.setup'),
            onClick: handleAutoPayActivationClick,
          },
        };
      }

      return { description: formatMessage('activities.payDashboard.eBillCellBadge.popover.active.description') };
    }
  }

  return null;
};

export const getBadgeStatus = (vendor: Vendor): BadgeProps['status'] => {
  switch (vendor.eBillStatus) {
    case VendorEBillStatusEnum.Capable:
      return 'neutral';
    case VendorEBillStatusEnum.Pending:
      return 'warning';
    case VendorEBillStatusEnum.Rejected:
      return 'critical';
    case VendorEBillStatusEnum.Active:
      return 'success';
  }
  return 'neutral';
};
